import { isMarketplaceBucketItem, TCreateSendBucketItem } from '../types/bucket';
import { IMarketplaceItem, IMarketplaceSupplier } from '../types/marketplace';

/** An empty `code` is the unknown-supplier lock. */
export interface IMarketplaceSupplierLock {
  code: string;
  name: string;
}

interface ISupplierSource {
  supplier_code?: string;
  supplier_name?: string;
}

export enum SupplierMatchEnum {
  Same = 'same',
  Different = 'different',
}

/** Mirrors the BE `NormalizeSupplierCode`. */
export const normalizeSupplierCode = (code?: string | null): string => (code ?? '').trim().toLowerCase();

/** A name without a code cannot take part in the lock, so it is dropped too. */
export const toMarketplaceSupplier = ({
  supplier_code,
  supplier_name,
}: ISupplierSource): IMarketplaceSupplier | null => {
  const code = (supplier_code ?? '').trim();

  if (!code) {
    return null;
  }

  const name = (supplier_name ?? '').trim();

  return name ? { code, name } : { code };
};

export const getItemSupplierCode = (item: Pick<IMarketplaceItem, 'supplier'>): string =>
  normalizeSupplierCode(item.supplier?.code);

/** The BE rejects a multi-item cart it cannot prove is single-supplier, so an unknown item admits nothing. */
export const resolveMarketplaceSupplierLock = (
  items: readonly TCreateSendBucketItem[],
): IMarketplaceSupplierLock | null => {
  const marketplaceItems = items.filter(isMarketplaceBucketItem);

  if (!marketplaceItems.length) {
    return null;
  }

  if (!marketplaceItems.every(getItemSupplierCode)) {
    return { code: '', name: '' };
  }

  const [locking] = marketplaceItems;

  return { code: getItemSupplierCode(locking), name: locking.supplier?.name ?? '' };
};

export const isUnknownSupplierLock = (lock: IMarketplaceSupplierLock | null): boolean => Boolean(lock) && !lock?.code;

/** Callers exempt items already in the send. */
export const isLockedOutBySupplier = (
  item: Pick<IMarketplaceItem, 'supplier'>,
  lock: IMarketplaceSupplierLock | null,
): boolean => {
  if (!lock) {
    return false;
  }

  const code = getItemSupplierCode(item);

  return !lock.code || code !== lock.code;
};

const toComparableName = (name?: string): string => (name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Near-duplicates ("W&P" / "W&P Design") count as the brand. A stopgap until the BE sends a flag. */
export const isSupplierTheBrand = (brand?: string, supplierName?: string): boolean => {
  const comparableBrand = toComparableName(brand);
  const comparableSupplier = toComparableName(supplierName);

  if (!comparableBrand || !comparableSupplier) {
    return true;
  }

  return comparableSupplier.startsWith(comparableBrand) || comparableBrand.startsWith(comparableSupplier);
};

/** "P. F. Candle Co." would otherwise double the period before a sentence stop. */
export const toSentenceName = (name: string): string => name.replace(/\.$/, '');
